import { type QuantitativeSize } from '../../types/sizes';

import { ButtonDiscreteSize, type ButtonVariant } from './types';

/**
 * Defines the default variant, {@link ButtonVariant}, that will be used in the case that the
 * 'variant' prop is not provided to the {@link Button}.  This value is chosen to be consistent
 * with the default value that MUI uses in their underlying button component.
 *
 * @see https://mui.com/material-ui/api/button/#props
 */
export const DefaultButtonVariant: ButtonVariant = 'text';

const DefaultButtonHorizontalPadding = 6;

/**
 * Defines properties that depend on the 'size' prop for both the {@link IconButton} and the
 * {@link Button} component.
 *
 * Eventually, this should be moved to the MUI `theme.ts` file, but it is temporarily defined here
 * in an effort to avoid changing global styles across all buttons in the application, allowing the
 * internalized forms of the {@link IconButton} and {@link Button} to be incrementally adopted.
 *
 * Note: The padding and line-height for the {@link Button} component still need to be incorporated
 * into the fold - which will ensure that the 'height' is consistent.  Currently, MUI does not
 * define the 'height' property, but let's the height set automatically based on the padding and
 * the line-height of the text in the button.
 */
export const ButtonSizeProperties = {
  [ButtonDiscreteSize.Large]: {
    fontSize: 14,
    height: 40,
    iconGap: 8,
    iconSize: 24,
    padding: [DefaultButtonHorizontalPadding, 16],
  },
  [ButtonDiscreteSize.Medium]: {
    fontSize: 14,
    height: 36,
    iconGap: 8,
    iconSize: 22,
    padding: [DefaultButtonHorizontalPadding, 14],
  },
  [ButtonDiscreteSize.Small]: {
    fontSize: 14,
    height: 32,
    iconGap: 6,
    iconSize: 20,
    padding: [DefaultButtonHorizontalPadding, 12],
  },
  [ButtonDiscreteSize.XSmall]: {
    fontSize: 12,
    height: 30,
    iconGap: 6,
    iconSize: 18,
    padding: [DefaultButtonHorizontalPadding, 10],
  },
} as const satisfies Record<
  ButtonDiscreteSize,
  {
    fontSize: number;
    height: QuantitativeSize<'px'>;
    iconGap: number;
    iconSize: number;
    padding: [number, number];
  }
>;

export const DefaultIconButtonPadding = DefaultButtonHorizontalPadding;

export const InnerButtonTextDataAttrType = 'inner-button-text' as const;
export const InnerButtonTextDataAttrKey = 'data-attr-type' as const;

export const InnerButtonTextDataAttr =
  `${InnerButtonTextDataAttrKey}="${InnerButtonTextDataAttrType}"` as const;
